/**
 * BroadcastsTab - Manage game broadcasts and scheduler
 * Uses useBroadcasts hook and extracted components for maintainability
 */
import { motion } from "framer-motion";
import { Plus, RefreshCw, Upload, Archive, Moon, Clock, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";
import { useState, useEffect } from "react";
import axios from "axios";

import { 
  PastGameForm, BulkImportModal, BroadcastForm, CompactBroadcastRow,
  BulkActionBar, useBroadcasts
} from "./broadcasts";
import { StreamMonitorStatus } from "./StreamMonitorStatus";
import { API, getAuthHeader } from "./utils";

export const BroadcastsTab = ({ channels, onRefresh }) => {
  const b = useBroadcasts(onRefresh);
  const [offseason, setOffseason] = useState(false);
  const [togglingOffseason, setTogglingOffseason] = useState(false);
  const [activeFilter, setActiveFilter] = useState("scheduled");

  useEffect(() => {
    axios.get(`${API}/settings/offseason`).then(r => setOffseason(r.data?.enabled || false)).catch(() => {});
  }, []);

  const toggleOffseason = async () => {
    setTogglingOffseason(true);
    try {
      const res = await axios.put(`${API}/settings/offseason`, { enabled: !offseason }, { headers: getAuthHeader() });
      setOffseason(res.data.enabled);
    } catch (e) {
      alert("Failed to toggle offseason mode");
    } finally {
      setTogglingOffseason(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Scheduler</h2>
          <p className="text-zinc-500 text-sm">Create and manage game broadcasts</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Offseason Mode Toggle */}
          <button
            onClick={toggleOffseason}
            disabled={togglingOffseason}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase border transition-colors ${
              offseason
                ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                : "bg-zinc-800 text-zinc-400 border-white/10 hover:text-white"
            }`}
            data-testid="offseason-toggle"
          >
            <Moon className="w-4 h-4" />
            {offseason ? "Offseason: ON" : "Offseason: OFF"}
          </button>
          <Button onClick={b.handleSyncAllScores} className="flex items-center gap-2 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none border border-white/10">
            <RefreshCw className="w-4 h-4" /> Sync StatMoose
          </Button>
          <Button onClick={() => b.setShowImportModal(true)} className="flex items-center gap-2 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none border border-white/10" data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" /> Bulk Import
          </Button>
          <Button onClick={() => { b.setShowPastGameForm(!b.showPastGameForm); b.setShowForm(false); }} className="flex items-center gap-2 bg-zinc-700 text-white hover:bg-zinc-600 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none border border-white/10" data-testid="add-past-game-btn">
            <Archive className="w-4 h-4" /> Add Past Game
          </Button>
          <Button onClick={() => { b.setShowForm(!b.showForm); b.setShowPastGameForm(false); b.setEditingId(null); }} className="flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none">
            <Plus className="w-4 h-4" /> New Game
          </Button>
        </div>
      </div>

      <BulkImportModal isOpen={b.showImportModal} onClose={() => b.setShowImportModal(false)} onImportComplete={b.fetchBroadcasts} />

      {b.showPastGameForm && (
        <PastGameForm pastGameData={b.pastGameData} setPastGameData={b.setPastGameData} teams={b.teams} events={b.events} onSubmit={b.handleSavePastGame} onCancel={() => b.setShowPastGameForm(false)} submitting={b.savingPastGame} />
      )}

      {b.showForm && (
        <BroadcastForm
          formData={b.formData} setFormData={b.setFormData} editingId={b.editingId} channels={channels} events={b.events} teams={b.teams}
          onSubmit={b.handleCreate} onCancel={() => { b.setShowForm(false); b.setEditingId(null); }} saving={b.saving}
          newLink={b.newLink} setNewLink={b.setNewLink} addLink={b.addLink}
          handleExtractTeamColor={b.handleExtractTeamColor} extractingColor={b.extractingColor}
          selectTeam={b.selectTeam} setTeamTBD={b.setTeamTBD}
          handleGenerateThumbnail={b.handleGenerateThumbnail} generatingThumbnail={b.generatingThumbnail}
          handleThumbnailUpload={b.handleThumbnailUpload} uploadingThumbnail={b.uploadingThumbnail}
          thumbnailInputRef={b.thumbnailInputRef}
        />
      )}

      <StreamMonitorStatus />

      {/* Broadcasts List - Spreadsheet Style */}
      <div className="bg-zinc-900/50 border border-white/5">
        {/* Scheduled / Completed Tabs */}
        <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveFilter("scheduled")}
            className={`flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              activeFilter === "scheduled"
                ? "text-white border-b-2 border-orange-500 bg-white/5"
                : "text-zinc-500 hover:text-white"
            }`}
            data-testid="filter-scheduled"
          >
            <Clock className="w-3.5 h-3.5" />
            Scheduled
            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-zinc-800 border border-white/10">
              {b.broadcasts.filter(br => !br.is_completed).length}
            </span>
          </button>
          <button
            onClick={() => setActiveFilter("completed")}
            className={`flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              activeFilter === "completed"
                ? "text-white border-b-2 border-green-500 bg-white/5"
                : "text-zinc-500 hover:text-white"
            }`}
            data-testid="filter-completed"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Completed
            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-zinc-800 border border-white/10">
              {b.broadcasts.filter(br => br.is_completed).length}
            </span>
          </button>
        </div>

        <BulkActionBar
          selectedCount={b.selectedIds.size} onBulkDelete={b.handleBulkDelete}
          bulkEditField={b.bulkEditField} setBulkEditField={b.setBulkEditField}
          bulkEditValue={b.bulkEditValue} setBulkEditValue={b.setBulkEditValue}
          onBulkEdit={b.handleBulkEdit} onClear={() => b.setSelectedIds(new Set())}
        />
        
        {/* Table Header */}
        <div className="grid grid-cols-[30px_auto_1fr_100px_90px_80px_100px_auto] gap-2 items-center px-3 py-2 border-b border-white/10 text-[10px] font-bold uppercase tracking-widest text-zinc-500 bg-zinc-900">
          <div className="flex items-center justify-center">
            <input type="checkbox" checked={b.broadcasts.length > 0 && b.selectedIds.size === b.broadcasts.length} onChange={b.toggleSelectAll} className="w-3.5 h-3.5 accent-orange-500 cursor-pointer" data-testid="select-all-checkbox" />
          </div>
          <div className="w-20">Status</div>
          <div>Game</div>
          <div>Sport</div>
          <div>Date</div>
          <div>Time</div>
          <div className="text-center">Score</div>
          <div className="w-24">Actions</div>
        </div>
        
        {(() => {
          const filtered = activeFilter === "completed"
            ? b.broadcasts.filter(br => br.is_completed)
            : b.broadcasts.filter(br => !br.is_completed);
          
          return filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500">
                {activeFilter === "completed" ? "No completed games" : "No upcoming broadcasts scheduled"}
              </p>
            </div>
          ) : (
            <div>
              {filtered.map(broadcast => (
                <CompactBroadcastRow
                  key={broadcast.id} broadcast={broadcast}
                  isExpanded={b.expandedBroadcast === broadcast.id} isSelected={b.selectedIds.has(broadcast.id)}
                  onToggleExpand={b.toggleExpanded} onToggleSelect={b.toggleSelect}
                  onGoLive={b.handleGoLive} onEndLive={b.handleEndLive} onEdit={b.handleEdit}
                  onDelete={b.handleDelete} onMarkComplete={b.handleMarkComplete}
                  onSyncScores={b.handleSyncScores} syncingScores={b.syncingScores[broadcast.id]}
                  onArchiveToOnDemand={b.handleArchiveToOnDemand} archiving={b.archivingBroadcast[broadcast.id]}
                  vodForm={b.vodForm} setVodForm={b.setVodForm} savingVod={b.savingVod}
                  onSaveVod={b.handleSaveVod} onRefreshBroadcast={b.fetchBroadcasts}
                  onToggleHidden={b.handleToggleHidden}
                />
              ))}
            </div>
          );
        })()}
      </div>
    </motion.div>
  );
};

export default BroadcastsTab;
